import { useState, useCallback } from "react";

import {
  normalizeAdminRole,
  hasRole as roleHasRole,
  type AdminRole,
} from "@/lib/roles";
import {
  clearStoredEnvironmentState,
  getStoredCurrentEnvironment,
  getStoredEnvOptions,
  setStoredCurrentEnvironment,
  setStoredEnvironmentState,
  type AdminEnvironment,
} from "@/lib/environment";

// ---------------------------------------------------------------------------
// Re-export 类型 — 旧调用方还在用 `admin_role / AdminRole`，保持源码兼容
// ---------------------------------------------------------------------------
/** @deprecated 使用 `AdminRole`（从 @/lib/roles 导入） */
export type admin_role = AdminRole;
export type { AdminRole };

export type ModuleKey = "crypto" | "us_stock" | "forex" | "ai_image" | "tianji";

export interface AdminUser {
  id: string;
  email: string;
  /** 必须是 ADMIN_ROLES 之一；从 storage 读取时由 normalizeAdminRole 兜底 */
  role: AdminRole;
  totp_verified: boolean;
  is_active: boolean;
  env_permissions?: Record<string, string>;
}

export type { AdminEnvironment };

const STORAGE_KEY = "quantum_admin";
const STORAGE_TOKEN_KEY = "quantum_token";
const MODULE_KEY = "quantum_module";

// 历史值（含旧大写枚举 / owner / viewer 等）一律走 normalizeAdminRole 归一化；
// 未知值兜底为 "admin"（最低写权限），避免 canAccess 永远 false 导致登录态半瘫痪
function safeNormalizeRole(raw: unknown): AdminRole {
  if (typeof raw !== "string") return "admin";
  return normalizeAdminRole(raw) ?? "admin";
}

function getStoredAdmin(): AdminUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdminUser;
    return { ...parsed, role: safeNormalizeRole(parsed.role as unknown) };
  } catch {
    return null;
  }
}

function setStoredAdmin(user: AdminUser | null) {
  if (user) {
    const normalized: AdminUser = {
      ...user,
      role: safeNormalizeRole(user.role as unknown),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

function getStoredModule(): ModuleKey {
  try {
    const raw = localStorage.getItem(MODULE_KEY) as ModuleKey;
    if (
      raw === "us_stock" ||
      raw === "forex" ||
      raw === "ai_image" ||
      raw === "tianji"
    ) {
      return raw;
    }
  } catch {
    // ignore
  }
  return "crypto";
}

// ---------------------------------------------------------------------------
// 权限矩阵（以 3 角色为准）
// ---------------------------------------------------------------------------
// 设计：
//   - super_admin: 所有模块 + 所有高危操作
//   - admin:       业务模块可见，账号管理等高危系统入口受限
//   - customer_support: 只能看用户 / dashboard / ai_users 几个客服相关
const MODULE_PERMISSIONS: Record<string, readonly AdminRole[]> = {
  crypto: ["super_admin", "admin", "customer_support"],
  dashboard: ["super_admin", "admin", "customer_support"],
  users: ["super_admin", "admin", "customer_support"],
  agents: ["super_admin", "admin"],
  settlements: ["super_admin", "admin", "customer_support"],
  withdrawals: ["super_admin", "admin"],
  risk: ["super_admin", "admin"],
  // config：admin可读；admin_accounts 仅 super_admin 作为全局系统管理入口
  config: ["super_admin", "admin"],
  admin_accounts: ["super_admin"],
  us_stock: ["super_admin", "admin"],
  forex: ["super_admin", "admin"],
  ai_image: ["super_admin", "admin", "customer_support"],
  ai_dashboard: ["super_admin", "admin", "customer_support"],
  ai_users: ["super_admin", "admin", "customer_support"],
  ai_generations: ["super_admin", "admin"],
  ai_reports: ["super_admin", "admin"],
  ai_vip: ["super_admin", "admin"],
  ai_orders: ["super_admin", "admin"],
  ai_points: ["super_admin", "admin"],
  ai_config: ["super_admin", "admin"],
  ai_audit: ["super_admin", "admin"],
  tianji: ["super_admin", "admin", "customer_support"],
  tianji_payments: ["super_admin", "admin", "customer_support"],
  tianji_review: ["super_admin", "admin"],
  refunds: ["super_admin", "admin"],
  reconciliation: ["super_admin", "admin"],
  revenue_ledger: ["super_admin", "admin"],
};

export function useAdminAuth() {
  const [admin, setAdmin] = useState<AdminUser | null>(getStoredAdmin);
  const [isLoading, setIsLoading] = useState(false);
  const [currentModule, setCurrentModule] = useState<ModuleKey>(getStoredModule);
  const [envOptions, setEnvOptionsState] = useState<AdminEnvironment[]>(getStoredEnvOptions);
  const [currentEnv, setCurrentEnvState] = useState<AdminEnvironment | null>(
    getStoredCurrentEnvironment
  );

  const setEnvironmentState = useCallback(
    (options: AdminEnvironment[] = [], current: AdminEnvironment | null = null) => {
      setEnvOptionsState(options);
      const nextCurrent = current ?? options[0] ?? null;
      setCurrentEnvState(nextCurrent);
      setStoredEnvironmentState(options, nextCurrent);
    },
    []
  );

  const setCurrentEnv = useCallback((env: AdminEnvironment | null) => {
    setCurrentEnvState(env);
    setStoredCurrentEnvironment(env);
  }, []);

  const login = useCallback(
    (
      user: AdminUser,
      options: AdminEnvironment[] = [],
      current: AdminEnvironment | null = null
    ) => {
      const normalized: AdminUser = {
        ...user,
        role: safeNormalizeRole(user.role as unknown),
      };
      setAdmin(normalized);
      setStoredAdmin(normalized);
      setEnvironmentState(options, current);
    },
    [setEnvironmentState]
  );

  const logout = useCallback(() => {
    setAdmin(null);
    setStoredAdmin(null);
    setEnvOptionsState([]);
    setCurrentEnvState(null);
    clearStoredEnvironmentState();
    localStorage.removeItem(STORAGE_TOKEN_KEY);
  }, []);

  const setModule = useCallback((module: ModuleKey) => {
    setCurrentModule(module);
    localStorage.setItem(MODULE_KEY, module);
  }, []);

  const hasRole = useCallback(
    (roles: readonly AdminRole[]) => {
      if (!admin) return false;
      return roleHasRole(admin.role, roles);
    },
    [admin]
  );

  const canAccess = useCallback(
    (module: string) => {
      if (!admin) return false;
      const allowed = MODULE_PERMISSIONS[module];
      if (!allowed) return false;
      return allowed.includes(admin.role);
    },
    [admin]
  );

  return {
    admin,
    isLoading,
    setIsLoading,
    currentModule,
    envOptions,
    currentEnv,
    setModule,
    login,
    logout,
    setEnvironmentState,
    setCurrentEnv,
    hasRole,
    canAccess,
  };
}
